import { io } from 'socket.io-client';
import requests from './httpService';

let socket = null;
let currentNoteId = null;
/** Only this handler is removed when leaving a note room (avoids wiping other listeners). */
let noteRoomReceiveHandler = null;
/** School-wide note chat feed (NoteList badges when not in the note room). */
let schoolNoteFeedHandler = null;
let schoolFeedRoomJoined = false;
/** Unread badge events — fan out to multiple subscribers (Header + active page). */
const unreadUpdateSubs = new Set();
const unreadClearedSubs = new Set();
const newNoteSubs = new Set();
const onSocketUnreadUpdate = (payload) => {
  unreadUpdateSubs.forEach((cb) => {
    try {
      cb(payload);
    } catch (err) {
      console.error('onUnreadUpdate handler error:', err);
    }
  });
};
const onSocketUnreadCleared = (payload) => {
  unreadClearedSubs.forEach((cb) => {
    try {
      cb(payload);
    } catch (err) {
      console.error('onUnreadCleared handler error:', err);
    }
  });
};
const onSocketNewNote = (payload) => {
  newNoteSubs.forEach((cb) => {
    try {
      cb(payload);
    } catch (err) {
      console.error('onNewNote handler error:', err);
    }
  });
};

/**
 * Get socket server URL from API base URL
 */
const getSocketURL = () => {
  try {
    const baseURL = requests.baseURL();
    // Same-origin CRA proxy for REST (`/api`); Socket.IO talks to API host directly.
    if (!baseURL || baseURL.startsWith('/')) {
      return process.env.REACT_APP_SOCKET_URL || 'http://localhost:5057';
    }
    if (baseURL.endsWith('/api')) {
      return baseURL.replace(/\/api$/, '');
    }
    return baseURL.replace('/api', '') || 'http://localhost:5000';
  } catch (err) {
    console.error('Error getting socket URL:', err);
    return 'http://localhost:5000';
  }
};

let connectingPromise = null;

/**
 * Initialize socket connection using a short-lived ticket from the HttpOnly cookie session.
 * @returns {Promise<import('socket.io-client').Socket|null>}
 */
const initializeSocket = () => {
  if (socket && socket.connected) {
    return Promise.resolve(socket);
  }
  if (connectingPromise) {
    return connectingPromise;
  }

  connectingPromise = (async () => {
    let token = null;
    try {
      const ticket = await requests.post('/user/socket-ticket', {});
      token = ticket?.token;
    } catch (err) {
      console.error('Failed to fetch socket ticket:', err);
      return null;
    }
    if (!token) {
      console.error('No socket ticket for authentication');
      return null;
    }

    const socketURL = getSocketURL();
    console.log('Connecting to socket server:', socketURL);

    socket = io(socketURL, {
      auth: {
        token,
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      if (schoolFeedRoomJoined) {
        socket.emit('join-school-note-feed');
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    socket.off('unread-update', onSocketUnreadUpdate);
    socket.on('unread-update', onSocketUnreadUpdate);
    socket.off('unread-cleared', onSocketUnreadCleared);
    socket.on('unread-cleared', onSocketUnreadCleared);
    socket.off('new-note', onSocketNewNote);
    socket.on('new-note', onSocketNewNote);
    socket.off('school-new-note', onSocketNewNote);
    socket.on('school-new-note', onSocketNewNote);

    return socket;
  })().finally(() => {
    connectingPromise = null;
  });

  return connectingPromise;
};

/**
 * Subscribe to unread bumps from any thread the current user can receive.
 * Returns an unsubscribe function.
 */
const onUnreadUpdate = (handler) => {
  if (typeof handler !== 'function') return () => {};
  // Make sure socket is up so events actually flow.
  if (!socket || !socket.connected) {
    initializeSocket().catch(() => {});
  }
  unreadUpdateSubs.add(handler);
  return () => unreadUpdateSubs.delete(handler);
};

const offUnreadUpdate = (handler) => {
  if (handler) unreadUpdateSubs.delete(handler);
  else unreadUpdateSubs.clear();
};

/** Subscribe to "this thread is now read" notifications (fired across the user's devices). */
const onUnreadCleared = (handler) => {
  if (typeof handler !== 'function') return () => {};
  if (!socket || !socket.connected) {
    initializeSocket().catch(() => {});
  }
  unreadClearedSubs.add(handler);
  return () => unreadClearedSubs.delete(handler);
};

const offUnreadCleared = (handler) => {
  if (handler) unreadClearedSubs.delete(handler);
  else unreadClearedSubs.clear();
};

const onNewNote = (handler) => {
  if (typeof handler !== 'function') return () => {};
  if (!socket || !socket.connected) {
    initializeSocket().catch(() => {});
  }
  newNoteSubs.add(handler);
  return () => newNoteSubs.delete(handler);
};

const offNewNote = (handler) => {
  if (handler) newNoteSubs.delete(handler);
  else newNoteSubs.clear();
};

/**
 * Mark a thread read via socket. Returns true if emitted, false if caller should
 * fall back to the REST endpoint.
 */
const markNoteReadSocket = (noteId) => {
  if (!noteId) return false;
  if (!socket || !socket.connected) return false;
  socket.emit('mark-note-read', { noteId });
  return true;
};

/**
 * Join a note chat room
 */
const joinNoteChat = async (noteId, callbacks = {}) => {
  if (!socket || !socket.connected) {
    socket = await initializeSocket();
    if (!socket) {
      console.error('Failed to initialize socket');
      if (callbacks.onError) callbacks.onError({ message: 'Failed to connect to chat server' });
      return;
    }
  }

  // Leave previous room if switching notes
  if (currentNoteId && currentNoteId !== noteId) {
    socket.emit('leave-note-chat', { noteId: currentNoteId });
  }

  currentNoteId = noteId;

  // Set up event listeners
  if (callbacks.onJoined) {
    socket.once('joined-room', callbacks.onJoined);
  }

  if (callbacks.onMessage) {
    if (noteRoomReceiveHandler) {
      socket.off('receive-message', noteRoomReceiveHandler);
    }
    noteRoomReceiveHandler = callbacks.onMessage;
    socket.on('receive-message', noteRoomReceiveHandler);
  }

  if (callbacks.onError) {
    socket.on('error', callbacks.onError);
  }

  if (callbacks.onUserJoined) {
    socket.on('user-joined', callbacks.onUserJoined);
  }

  // Join the room
  socket.emit('join-note-chat', { noteId });
  console.log('Joining note chat room:', noteId);
};

/**
 * Send a message via socket
 */
const sendMessage = (noteId, message, callbacks = {}) => {
  if (!socket || !socket.connected) {
    console.error('Socket not connected');
    if (callbacks.onError) {
      callbacks.onError({ message: 'Not connected to chat server' });
    }
    return;
  }

  socket.emit('send-message', { noteId, message }, (response) => {
    if (response && response.error) {
      if (callbacks.onError) {
        callbacks.onError(response);
      }
    } else if (callbacks.onSuccess) {
      callbacks.onSuccess(response);
    }
  });
};

/**
 * Leave a note chat room
 */
const leaveNoteChat = (noteId) => {
  if (socket && socket.connected) {
    socket.emit('leave-note-chat', { noteId });
    if (noteRoomReceiveHandler) {
      socket.off('receive-message', noteRoomReceiveHandler);
      noteRoomReceiveHandler = null;
    }
    if (currentNoteId === noteId) {
      currentNoteId = null;
    }
    console.log('Left note chat room:', noteId);
  }
};

/**
 * Disconnect socket
 */
const disconnectSocket = () => {
  if (socket) {
    schoolFeedRoomJoined = false;
    schoolNoteFeedHandler = null;
    noteRoomReceiveHandler = null;
    unreadUpdateSubs.clear();
    unreadClearedSubs.clear();
    newNoteSubs.clear();
    socket.disconnect();
    socket = null;
    currentNoteId = null;
    console.log('Socket disconnected');
  }
};

/**
 * Join school-wide room so staff see parent/teacher chat activity without opening each thread.
 */
const joinSchoolNoteFeed = async (callbacks = {}) => {
  if (!socket || !socket.connected) {
    socket = await initializeSocket();
    if (!socket) {
      if (callbacks.onError) callbacks.onError({ message: 'Failed to connect to chat server' });
      return;
    }
  }
  if (callbacks.onMessage) {
    if (schoolNoteFeedHandler) {
      socket.off('school-note-chat-message', schoolNoteFeedHandler);
    }
    schoolNoteFeedHandler = callbacks.onMessage;
    socket.on('school-note-chat-message', schoolNoteFeedHandler);
  }
  schoolFeedRoomJoined = true;
  socket.emit('join-school-note-feed');
};

const leaveSchoolNoteFeed = () => {
  schoolFeedRoomJoined = false;
  if (socket && socket.connected) {
    socket.emit('leave-school-note-feed');
    if (schoolNoteFeedHandler) {
      socket.off('school-note-chat-message', schoolNoteFeedHandler);
      schoolNoteFeedHandler = null;
    }
  }
};

/**
 * Check if socket is connected
 */
const isConnected = () => {
  return socket && socket.connected;
};

/**
 * Join parent's push notification room
 */
const joinPushNotifications = async (callbacks = {}) => {
  if (!socket || !socket.connected) {
    socket = await initializeSocket();
    if (!socket) {
      if (callbacks.onError) callbacks.onError({ message: 'Failed to connect to server' });
      return;
    }
  }

  if (callbacks.onJoined) {
    socket.once('joined-push-room', callbacks.onJoined);
  }
  if (callbacks.onNotification) {
    socket.on('push-notification', callbacks.onNotification);
  }
  if (callbacks.onError) {
    socket.on('error', callbacks.onError);
  }

  socket.emit('join-push-notifications');
};

const socketService = {
  initializeSocket,
  joinNoteChat,
  sendMessage,
  leaveNoteChat,
  joinSchoolNoteFeed,
  leaveSchoolNoteFeed,
  joinPushNotifications,
  disconnectSocket,
  isConnected,
  onUnreadUpdate,
  offUnreadUpdate,
  onUnreadCleared,
  offUnreadCleared,
  onNewNote,
  offNewNote,
  markNoteReadSocket,
  getSocket: () => socket,
};

export default socketService;

